import type { SvrEdgeNormal, SvrTransformedNode } from '@poseidon/types/svr/svrGraph.type';
import type { PuppedGraphs, UIPoint, UIRect, UISize } from '@poseidon/types/types/types';

export const dotProduct = (n1: UIPoint, n2: UIPoint) => n1.x * n2.x + n1.y * n2.y;

export const normalToXY = (normal: SvrEdgeNormal) => {
  switch (normal) {
    case 'up':
      return { x: 0, y: -1 };
    case 'right':
      return { x: 1, y: 0 };
    case 'down':
      return { x: 0, y: 1 };
    case 'left':
      return { x: -1, y: 0 };
  }
};

// export const squarePath = ({
//   from,
//   to,
//   fNormal,
//   tNormal,
//   offset,
// }: {
//   from: UIPoint;
//   to: UIPoint;
//   fNormal: SvrEdgeNormal;
//   tNormal: SvrEdgeNormal;
//   offset: number;
// }) => {
//   const fNormalXY = normalToXY(fNormal);
//   const tNormalXY = normalToXY(tNormal);
//   const m = getMidpoint({ from, to, fNormal, tNormal, offset });

//   return [
//     `M ${from.x} ${from.y}`,
//     `L ${from.x + fNormalXY.x * offset} ${from.y + fNormalXY.y * offset}`,
//     `L ${m.x} ${m.y}`,
//     `L ${to.x + tNormalXY.x * offset} ${to.y + tNormalXY.y * offset}`,
//     `L ${to.x} ${to.y}`,
//   ].join(' ');
// };

export const squarePath2 = ({
  from,
  to,
  fNormal,
  tNormal,
  offset,
}: {
  from: UIPoint;
  to: UIPoint;
  fNormal: SvrEdgeNormal;
  tNormal: SvrEdgeNormal;
  offset: number;
}) => {
  const fNormalXY = normalToXY(fNormal);
  const tNormalXY = normalToXY(tNormal);
  // const m = getMidpoint({ from, to, fNormal, tNormal, offset });
  const fNormalPoint = { x: from.x + fNormalXY.x * offset, y: from.y + fNormalXY.y * offset };
  const tNormalPoint = { x: to.x + tNormalXY.x * offset, y: to.y + tNormalXY.y * offset };

  const xDiff = Math.abs(tNormalPoint.x - fNormalPoint.x);
  const yDiff = Math.abs(tNormalPoint.y - fNormalPoint.y);

  if (xDiff > yDiff) {
    return [
      `M ${from.x} ${from.y}`,
      `L ${fNormalPoint.x} ${fNormalPoint.y}`,
      `L ${tNormalPoint.x} ${fNormalPoint.y}`,
      `L ${tNormalPoint.x} ${tNormalPoint.y}`,
      `L ${to.x} ${to.y}`,
    ].join(' ');
  } else {
    return [
      `M ${from.x} ${from.y}`,
      `L ${fNormalPoint.x} ${fNormalPoint.y}`,
      `L ${fNormalPoint.x} ${tNormalPoint.y}`,
      `L ${tNormalPoint.x} ${tNormalPoint.y}`,
      `L ${to.x} ${to.y}`,
    ].join(' ');
  }
};

export const getMidpoint2 = ({
  from,
  to,
  fNormal,
  tNormal,
  offset,
}: {
  from: UIPoint;
  to: UIPoint;
  fNormal: SvrEdgeNormal;
  tNormal: SvrEdgeNormal;
  offset: number;
}) => {
  const fNormalXY = normalToXY(fNormal);
  const tNormalXY = normalToXY(tNormal);
  const fNormalPoint = { x: from.x + fNormalXY.x * offset, y: from.y + fNormalXY.y * offset };
  const tNormalPoint = { x: to.x + tNormalXY.x * offset, y: to.y + tNormalXY.y * offset };

  const xDiff = Math.abs(tNormalPoint.x - fNormalPoint.x);
  const yDiff = Math.abs(tNormalPoint.y - fNormalPoint.y);

  const points =
    xDiff > yDiff
      ? [
          { x: from.x, y: from.y },
          { x: fNormalPoint.x, y: fNormalPoint.y },
          { x: tNormalPoint.x, y: fNormalPoint.y },
          { x: tNormalPoint.x, y: tNormalPoint.y },
          { x: to.x, y: to.y },
        ]
      : [
          { x: from.x, y: from.y },
          { x: fNormalPoint.x, y: fNormalPoint.y },
          { x: fNormalPoint.x, y: tNormalPoint.y },
          { x: tNormalPoint.x, y: tNormalPoint.y },
          { x: to.x, y: to.y },
        ];

  const totalDistance = points.reduce((acc, point, i) => {
    if (i === 0) return 0;
    const prevPoint = points[i - 1];
    if (!prevPoint) return acc;
    const xDiff = Math.abs(point.x - prevPoint.x);
    const yDiff = Math.abs(point.y - prevPoint.y);
    return acc + xDiff + yDiff;
  }, 0);

  let xMidDiff = 0;
  let yMidDiff = 0;
  let xMidDistance = 0;
  let yMidDistance = 0;

  for (let i = 1; i < points.length; i++) {
    const point = points[i];

    if (!point) continue;

    const prevPoint = points[i - 1];
    if (!prevPoint) continue;

    const xPointDiff = Math.abs(point.x - prevPoint.x);
    const yPointDiff = Math.abs(point.y - prevPoint.y);

    const halfDistance = totalDistance / 2;

    const remainingDistance = halfDistance - yMidDistance - xMidDistance;
    if (xPointDiff >= remainingDistance) {
      xMidDistance = xMidDistance + Math.min(xPointDiff, remainingDistance);
      if (point.x - prevPoint.x < 0) {
        xMidDiff = xMidDiff - Math.min(-(point.x - prevPoint.x), remainingDistance);
      } else {
        xMidDiff = xMidDiff + Math.min(point.x - prevPoint.x, remainingDistance);
      }
      break;
    } else if (yPointDiff >= remainingDistance) {
      yMidDistance = yMidDistance + Math.min(yPointDiff, remainingDistance);
      if (point.y - prevPoint.y < 0) {
        yMidDiff = yMidDiff - Math.min(-(point.y - prevPoint.y), remainingDistance);
      } else {
        yMidDiff = yMidDiff + Math.min(point.y - prevPoint.y, remainingDistance);
      }
      break;
    }

    xMidDiff += point.x - prevPoint.x;
    yMidDiff += point.y - prevPoint.y;
    xMidDistance += xPointDiff;
    yMidDistance += yPointDiff;
  }

  return { x: xMidDiff + from.x, y: yMidDiff + from.y };
};

export const getWH = (from: UIPoint, to: UIPoint): UISize => ({
  w: Math.abs(to.x - from.x),
  h: Math.abs(to.y - from.y),
});
export const getDiagonal = (s: UISize) => {
  return Math.sqrt(Math.pow(s.w, 2) + Math.pow(s.h, 2));
};

export const getMidpoint = ({
  from,
  to,
  fNormal,
  tNormal,
  offset,
}: {
  from: UIPoint;
  to: UIPoint;
  fNormal: SvrEdgeNormal;
  tNormal: SvrEdgeNormal;
  offset: number;
}) => {
  const fNormalXY = normalToXY(fNormal);
  const tNormalXY = normalToXY(tNormal);
  const wh = getWH(from, to);
  const newFrom = { x: from.x + fNormalXY.x * offset, y: from.y + fNormalXY.y * offset };
  const newTo = { x: to.x + tNormalXY.x * offset, y: to.y + tNormalXY.y * offset };

  const fromPointToUse = getDiagonal(getWH(newFrom, to)) > getDiagonal(wh) ? newFrom : from;
  const toPointToUse = getDiagonal(getWH(newTo, from)) > getDiagonal(wh) ? newTo : to;

  const width = toPointToUse.x - fromPointToUse.x;
  const height = toPointToUse.y - fromPointToUse.y;

  return { x: fromPointToUse.x + width / 2, y: fromPointToUse.y + height / 2 };
};

export const anchorPt = (normal: SvrEdgeNormal, n: UIRect, index: number, total: number): UIPoint => {
  switch (normal) {
    case 'up':
      return { x: n.x + (n.w * (index + 1)) / (total + 1), y: n.y };
    case 'right':
      return {
        x: n.x + n.w,
        y: n.y + (n.h * (index + 1)) / (total + 1),
      };
    case 'down':
      return { x: n.x + (n.w * (index + 1)) / (total + 1), y: n.y + n.h };
    case 'left':
      return { x: n.x, y: n.y + (n.h * (index + 1)) / (total + 1) };
  }
};

export function keyByFunction<A extends object, K extends PropertyKey>(array: A[], keyFn: (x: A) => K) {
  return array.reduce((r, x) => ({ ...r, [keyFn(x)]: x }), {} as { [P in K]: A });
}

export function transformGraphs(
  graphs: { nodes: SvrTransformedNode[]; title: string; startId: string | null; id: string }[],
): {
  graphs: PuppedGraphs;
  startId: string | undefined;
} {
  const mappedGraphs = graphs
    .map((graph) =>
      graph.startId == null
        ? undefined
        : {
            startId: graph.startId,
            id: graph.id,
            title: graph.title,
            nodes: keyByFunction(graph.nodes, (n) => n.id),
          },
    )
    .filter((a) => a != null);
  return { graphs: keyByFunction(mappedGraphs, (g) => g.id), startId: mappedGraphs[0]?.id };
}
